import S3 from 'aws-sdk/clients/s3';
import { fetchAuthSession } from 'aws-amplify/auth';
import { gql } from '@apollo/client';
import config from 'aws-exports';

import { apolloClient } from 'core/apollo-client';

// GraphQL queries using existing operations
const GET_POLICY_TASK_DOCUMENTS = gql`
  query GetAccountTaskDocuments($accountId: ID!) {
    getDocs(accountId: $accountId) {
      internal {
        path
        lastModified
      }
    }
  }
`;

const GET_UPLOAD_URL = gql`
  query GetUploadUrl($accountId: ID!, $fileName: String!, $mimeType: String!) {
    getUploadUrl(accountId: $accountId, fileName: $fileName, mimeType: $mimeType) {
      url
    }
  }
`;

export const uploadTaskDocument = async (accountId, file, s3Path, onProgress) => {
  try {
    const desiredFileName = s3Path.split('/').pop();

    //Get upload URL
    const { data: uploadUrlData } = await apolloClient.query({
      query: GET_UPLOAD_URL,
      variables: {
        accountId,
        fileName: desiredFileName,
        mimeType: file.type
      }
    });

    if (!uploadUrlData?.getUploadUrl?.url) {
      console.error('No upload URL in response:', uploadUrlData);
      throw new Error('No upload URL returned from server');
    }

    const { url: uploadUrl } = uploadUrlData.getUploadUrl;

    //Upload file to S3
    await uploadFileToS3(uploadUrl, file, onProgress);

    // Step 3: Extract actual S3 key from presigned URL
    // The presigned URL contains the actual path where the file was uploaded
    let actualS3Key;
    try {
      const urlObj = new URL(uploadUrl);
      let pathname = decodeURIComponent(urlObj.pathname.substring(1)); // Remove leading '/'

      // If the pathname includes the bucket name (path-style URLs), strip it
      // Get bucket name from environment config instead of hardcoding
      const bucketName = config.Storage.bucket;
      if (bucketName && pathname.startsWith(bucketName)) {
        // Remove bucket name from the beginning of the path
        pathname = pathname.substring(bucketName.length);
        // Remove leading slash if present
        if (pathname.startsWith('/')) {
          pathname = pathname.substring(1);
        }
      }

      actualS3Key = pathname;

      // Validate that we extracted a valid S3 key
      if (!actualS3Key || actualS3Key.trim() === '') {
        throw new Error('Extracted S3 key is empty');
      }
    } catch (error) {
      console.error('Failed to extract S3 key from presigned URL:', {
        error,
        uploadUrl,
        providedPath: s3Path
      });
      throw new Error('Could not determine uploaded file location. Please try again.');
    }

    // Return document info with actual S3 path
    return {
      id: actualS3Key,
      documentUrl: actualS3Key,
      fileName: desiredFileName,
      uploadedAt: new Date().toISOString()
    };
  } catch (error) {
    console.error('Error uploading task document:', error);
    throw error;
  }
};

/**
 * Upload file to S3 using presigned URL
 */
const uploadFileToS3 = (uploadUrl, file, onProgress) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(event);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

/**
 * Fetch all documents for a accountId
 */
export const fetchAccountTaskDocuments = async (accountId) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_POLICY_TASK_DOCUMENTS,
      variables: { accountId },
      fetchPolicy: 'no-cache' // Always fetch fresh data
    });

    const documents = data.getDocs?.internal || [];

    // Transform the documents to match expected format
    return documents.map((doc) => {
      const fileName = doc.path.split('/').pop(); // Extract filename from path
      return {
        id: doc.path,
        fileName,
        uploadedAt: doc.lastModified,
        path: doc.path
      };
    });
  } catch (error) {
    console.error('Error fetching offer documents:', error);
    throw new Error('Failed to fetch documents');
  }
};

/**
 * Download a document
 */
export const downloadTaskDocument = async (path, secondsBeforeExpire = 86400) => {
  try {
    const { credentials } = await fetchAuthSession();
    const s3Client = new S3({
      region: config.Storage.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
      }
    });
    const url = s3Client.getSignedUrl('getObject', {
      Key: path,
      Bucket: config.Storage.bucket,
      Expires: secondsBeforeExpire
    });
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error downloading document:', error);
  }
};
